import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useInhouseEmails from "hooks/Inhouse emails/useInhouseEmails";
import EmailAccountsTable from "./EmailAccountsTable";
import ViewEmailAccountModal from "./ViewEmailAccountModal";
import CreateEmailAccountModal from "./CreateEmailAccountModal";

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  const data = payload?.data ?? payload;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

export default function EmailAccounts() {
  const {
    getAllAccounts,
    getAccountById,
    createAccount,
    deleteAccountById,
    deleteAccountByEmail,
  } = useInhouseEmails();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [showView, setShowView] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllAccounts();
      setAccounts(normalizeList(response));
    } catch (err) {
      toast.error(err?.message || "Failed to load email accounts", {
        theme: "colored",
      });
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [getAllAccounts]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const pagedAccounts = useMemo(() => {
    const start = page * pageSize;
    return accounts.slice(start, start + pageSize);
  }, [accounts, page, pageSize]);

  const totalPages = useMemo(() => {
    if (!accounts.length) return 1;
    return Math.ceil(accounts.length / pageSize);
  }, [accounts.length, pageSize]);

  const handleView = async (id) => {
    if (!id) return;
    setShowView(true);
    setViewLoading(true);
    try {
      const record = await getAccountById(id);
      setSelectedAccount(record || null);
    } catch (err) {
      toast.error(err?.message || "Failed to load account details", {
        theme: "colored",
      });
      setSelectedAccount(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleCreate = async (payload) => {
    setCreating(true);
    try {
      await createAccount(payload);
      toast.success("Email account created", { theme: "colored" });
      setShowCreate(false);
      await loadAccounts();
    } catch (err) {
      toast.error(err?.message || "Failed to create email account", {
        theme: "colored",
      });
    } finally {
      setCreating(false);
    }
  };

  const openDelete = (mode, value) => {
    setDeleteTarget({ mode, value });
    setShowDelete(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.mode === "email") {
        await deleteAccountByEmail({ email: deleteTarget.value });
      } else {
        await deleteAccountById(deleteTarget.value);
      }
      toast.success("Email account deleted", { theme: "colored" });
      await loadAccounts();
    } catch (err) {
      toast.error(err?.message || "Failed to delete email account", {
        theme: "colored",
      });
    } finally {
      setShowDelete(false);
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <WidgetSectionTitle
        icon="address-book"
        title="Email Accounts"
        subtitle="Manage email accounts and view account details."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <Row className="align-items-center mb-3 g-2">
        <Col xs="auto">
          <Button variant="success" size="sm" onClick={() => setShowCreate(true)}>
            Add Email Account
          </Button>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" size="sm" onClick={loadAccounts}>
            Refresh
          </Button>
        </Col>
        {loading && (
          <Col xs="auto">
            <Spinner animation="border" size="sm" />
          </Col>
        )}
      </Row>

      <EmailAccountsTable
        accounts={pagedAccounts}
        loading={loading}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalElements={accounts.length}
        setPage={setPage}
        setPageSize={setPageSize}
        onView={handleView}
        onDeleteById={(id) => openDelete("id", id)}
        onDeleteByEmail={(email) => openDelete("email", email)}
      />

      <ViewEmailAccountModal
        show={showView}
        onClose={() => {
          setShowView(false);
          setSelectedAccount(null);
        }}
        account={selectedAccount}
        loading={viewLoading}
      />

      <CreateEmailAccountModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
        loading={creating}
      />

      <ConfirmDeleteModal
        show={showDelete}
        setShow={setShowDelete}
        onConfirm={handleDelete}
        itemName="email account"
      />
    </>
  );
}
